// src/components/MsoTestPanel.tsx
//
// ★★ ANNEX A 22 — the rule's test cases and the result of running them against a draft.
//
// ══ WHY THE EVIDENCE IS SHOWN AGAINST THE DEFINITION IT WAS RUN ON ══
//
// A test run is bound to the definition HASH it ran against. Edit a tested draft and the hash
// changes, so the earlier run no longer counts and Activate says so (`lib/mso-editor.ts`). This
// panel shows the latest run FOR THE CURRENT HASH, and says plainly when there is none, so an
// officer is never reading a green result that describes a definition that no longer exists.
//
// ⚠ Per case it shows expected against actual, side by side, in words ("Passed", "Failed") and
// never by colour alone, with the engine's own trace one click away.
import { plainProse } from '../lib/plain-words'
import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { IconButton } from './IconButton'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { formatCents, formatInstant } from '../lib/format'
import { dollarsToCents, msoRefusalLabel, MSO_CALCULATION_REFUSALS } from '../lib/mso-vocabulary'
import { ConfirmWrite } from './ConfirmWrite'
import { FormResult, type FormResultState } from './FormResult'
import { Money } from './Money'
import { CategoryBadge } from './StatusBadge'
import { MsoTrace } from './MsoTrace'
import type { MsoCaseResult, MsoFieldBounds, MsoRuleVersion, MsoTestCase, MsoTestRun } from '../lib/types'

const SOURCE_WORDS: Readonly<Record<string, string>> = {
  AUTHORITY_PACK: 'Standard pack',
  AUTHOR: 'Added by PSD',
}

function expectedText(c: { expectedOfficerCents: number | null; expectedGovernmentCents: number | null; expectedRefusalKind: string | null }) {
  if (c.expectedRefusalKind !== null) return `Refused: ${msoRefusalLabel(c.expectedRefusalKind)}`
  return null
}

function ResultLine({ result }: { result: MsoCaseResult }) {
  const { expected, actual } = result
  return (
    <li className="pt-well p-3" data-case={result.caseId}>
      <p className="m-0 font-semibold">
        <span className="pt-mono">{result.caseId}</span>{' '}
        <CategoryBadge kind="verdict" value={result.passed ? 'PASSED' : 'FAILED'} label={result.passed ? 'Passed' : 'Failed'} />
      </p>
      <div className="pt-table-wrap mt-2">
        <table className="pt-table">
          <caption className="sr-only">Expected and actual figures for {result.caseId}</caption>
          <thead>
            <tr>
              <th scope="col" data-unsortable="a case result compares expected with actual in a fixed layout; sorting its three rows would break the comparison">
                Figure
              </th>
              <th scope="col" data-unsortable="a case result compares expected with actual in a fixed layout; sorting its three rows would break the comparison">
                Expected
              </th>
              <th scope="col" data-unsortable="a case result compares expected with actual in a fixed layout; sorting its three rows would break the comparison">
                Actual
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">Officer or dependant</th>
              <td>
                <Money cents={expected.officerCents} placeholder="Not expected" />
              </td>
              <td>
                <Money cents={actual.officerCents} placeholder="None" />
              </td>
            </tr>
            <tr>
              <th scope="row">Government</th>
              <td>
                <Money cents={expected.governmentCents} placeholder="Not expected" />
              </td>
              <td>
                <Money cents={actual.governmentCents} placeholder="None" />
              </td>
            </tr>
            <tr>
              <th scope="row">Refusal</th>
              <td>{expected.refusalKind === null ? 'None expected' : <CategoryBadge kind="refusal" value={expected.refusalKind} label={msoRefusalLabel(expected.refusalKind)} />}</td>
              <td>{actual.refusalKind === null ? 'None' : <CategoryBadge kind="refusal" value={actual.refusalKind} label={msoRefusalLabel(actual.refusalKind)} />}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <details className="mt-2">
        <summary className="text-sm cursor-pointer">Trace for {result.caseId}</summary>
        <div className="mt-2">
          <MsoTrace trace={result.trace} heading={`Trace for ${result.caseId}`} />
        </div>
      </details>
    </li>
  )
}

export interface MsoTestPanelProps {
  readonly version: MsoRuleVersion
  readonly testCases: readonly MsoTestCase[]
  readonly testRuns: readonly MsoTestRun[]
  /** The result of a run made in this session, for the current hash. */
  readonly lastRun: { readonly run: MsoTestRun; readonly results: readonly MsoCaseResult[] } | null
  readonly dirty: boolean
  readonly editable: boolean
  /** Resolves true when the run finished, false when it could not be run (the page shows that error itself). */
  readonly onRun: () => Promise<boolean | void>
  readonly running: boolean
  readonly onCasesChanged: () => void
  /** The server's published bound for a case description. */
  readonly bounds?: MsoFieldBounds | undefined
  /** Raised by the page each time a run it started (the Run tests now control) finishes, so the result takes focus. */
  readonly resultFocusSignal?: number
}

export function MsoTestPanel({ version, testCases, testRuns, lastRun, dirty, editable, onRun, running, onCasesChanged, bounds, resultFocusSignal = 0 }: MsoTestPanelProps) {
  const baseId = useId()
  const [caseId, setCaseId] = useState('')
  const [description, setDescription] = useState('')
  const [inputText, setInputText] = useState('')
  const [expectation, setExpectation] = useState<'AMOUNT' | 'REFUSAL'>('AMOUNT')
  const [officer, setOfficer] = useState('')
  const [government, setGovernment] = useState('')
  const [refusalKind, setRefusalKind] = useState<string>(MSO_CALCULATION_REFUSALS[0])
  const [state, setState] = useState<FormResultState>({ status: 'idle' })
  // The case an officer has asked to remove, held until they confirm. Authority pack cases are never offered.
  const [removing, setRemoving] = useState<string | null>(null)
  const [removeState, setRemoveState] = useState<FormResultState>({ status: 'idle' })
  // ★ F-03: after Run tests the button is disabled while it runs, so it cannot keep focus. The pass or fail line is a
  // live region and takes focus when the run finishes, so the officer is told the result where they are.
  const resultRef = useRef<HTMLParagraphElement>(null)
  const [focusResult, setFocusResult] = useState(false)
  useEffect(() => {
    if (!focusResult || resultRef.current === null) return
    resultRef.current.focus()
    setFocusResult(false)
  })
  useEffect(() => {
    if (resultFocusSignal > 0) setFocusResult(true)
  }, [resultFocusSignal])
  const handleRunClick = async () => {
    const finished = await onRun()
    if (finished !== false) setFocusResult(true)
  }

  const cases = [...testCases].sort((a, b) => (a.caseId < b.caseId ? -1 : 1))

  // The latest run on the CURRENT definition: the one just made, else the newest stored one.
  const stored = [...testRuns]
    .filter((r) => r.definitionHash === version.definitionHash)
    .sort((a, b) => (a.runAt < b.runAt ? 1 : -1))[0]
  const current =
    lastRun !== null && lastRun.run.definitionHash === version.definitionHash
      ? lastRun
      : stored === undefined
        ? null
        : { run: stored, results: Array.isArray(stored.results) ? (stored.results as MsoCaseResult[]) : [] }

  const prefill = (id: string) => {
    const chosen = cases.find((c) => c.caseId === id)
    if (chosen !== undefined) setInputText(JSON.stringify(chosen.input, null, 2))
  }

  const performRemove = async (caseId: string) => {
    setRemoving(null)
    setRemoveState({ status: 'submitting' })
    try {
      await api.deleteMsoTestCase(version.ruleKey, caseId)
      setRemoveState({ status: 'success', message: `Test case ${caseId} removed. Run the tests again: the set of cases has changed.` })
      onCasesChanged()
    } catch (error) {
      setRemoveState({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not remove the test case.' })
    }
  }

  const handleAdd = async (event: FormEvent) => {
    event.preventDefault()
    let input: unknown
    try {
      input = JSON.parse(inputText)
    } catch {
      setState({ status: 'error', message: 'The case input is not valid JSON. Write money without a decimal point, for example 13520 for SGD 135.20.' })
      return
    }
    const body: Parameters<typeof api.addMsoTestCase>[1] = { caseId: caseId.trim().toUpperCase(), description: description.trim(), input }
    if (expectation === 'REFUSAL') {
      body.expectedRefusalKind = refusalKind
    } else {
      const o = dollarsToCents(officer)
      const g = dollarsToCents(government)
      if ('error' in o || 'error' in g) {
        setState({
          status: 'error',
          message: `The expected ${'error' in o ? 'officer or dependant' : 'Government'} amount: ${'error' in o ? o.error : (g as { error: string }).error}`,
        })
        return
      }
      body.expectedOfficerCents = o.cents
      body.expectedGovernmentCents = g.cents
    }
    setState({ status: 'submitting' })
    try {
      await api.addMsoTestCase(version.ruleKey, body)
      setState({ status: 'success', message: `Test case ${body.caseId} added. Run the tests to see it pass or fail.` })
      setCaseId('')
      setDescription('')
      setInputText('')
      setOfficer('')
      setGovernment('')
      onCasesChanged()
    } catch (error) {
      setState({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not add the test case.' })
    }
  }

  return (
    <section aria-label="Test cases" className="pt-card flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold mb-1">Test cases</h2>
        <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[75ch] m-0">
          The cases every version of {version.ruleKey} must satisfy before it can be activated. The standard pack cases are
          fixed; cases you add join them.
        </p>
      </div>

      {cases.length === 0 ? (
        <p className="text-sm">This rule has no test cases yet.</p>
      ) : (
        <ul className="flex flex-col gap-2 list-none p-0 m-0">
          {cases.map((c) => (
            <li key={c.caseId} className="text-sm" data-case-def={c.caseId}>
              <span className="pt-mono font-semibold">{c.caseId}</span>{' '}
              <span className="text-xs uppercase tracking-wide px-1 border border-[var(--sgds-border-color-default)] rounded">
                {SOURCE_WORDS[c.source] ?? c.source}
              </span>{' '}
              {plainProse(c.description)}{' '}
              <span className="text-[var(--sgds-body-color-subtle)]">
                Expects{' '}
                {expectedText(c) ??
                  `${c.expectedOfficerCents === null ? 'no figure' : formatCents(c.expectedOfficerCents)} officer or dependant, ${c.expectedGovernmentCents === null ? 'no figure' : formatCents(c.expectedGovernmentCents)} Government`}
                .
              </span>
              {c.source === 'AUTHOR' ? (
                editable && (
                  <>
                    {' '}
                    <IconButton glyph="trash" tone="danger" label={`Remove test case ${c.caseId}`} onClick={() => setRemoving(c.caseId)} />
                  </>
                )
              ) : (
                <span className="block text-xs text-[var(--sgds-body-color-subtle)]">
                  A standard pack case: it cannot be removed.
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {removing !== null && (
        <ConfirmWrite
          summary={`Remove test case ${removing} from ${version.ruleKey}. Every version of this rule is tested against the cases that remain, so the next test run no longer covers it.`}
          confirmLabel="Yes, remove it"
          variant="danger"
          busy={removeState.status === 'submitting'}
          onConfirm={() => void performRemove(removing)}
          onCancel={() => setRemoving(null)}
        />
      )}
      <FormResult state={removeState} focusOnResult />

      {editable && (
        <div className="flex flex-col gap-2">
          <div>
            <SgdsButton type="button" variant="primary" disabled={dirty || running} loading={running} onClick={() => void handleRunClick()}>
              Run tests
            </SgdsButton>
          </div>
          {dirty && (
            <p className="text-sm text-[var(--sgds-body-color-subtle)] m-0" id={`${baseId}-dirty`}>
              Save your changes first. The tests run against the saved definition.
            </p>
          )}
        </div>
      )}

      <div>
        {current === null ? (
          <p className="text-sm m-0" data-testid="no-run">
            {editable
              ? 'No test run covers this exact definition yet.'
              : 'Test runs are made on drafts. This version has been approved and is in force, so there is nothing to run.'}
          </p>
        ) : (
          <div className="flex flex-col gap-3" data-testid="latest-run">
            <p ref={resultRef} tabIndex={-1} role="status" aria-atomic="true" className="text-sm m-0">
              <CategoryBadge kind="verdict" value={current.run.passed ? 'PASSED' : 'FAILED'} label={current.run.passed ? 'Passed' : 'Failed'} />: the latest run on this definition,{' '}
              {formatInstant(current.run.runAt)}, covered {current.run.caseCount} case(s).
            </p>
            <ul className="flex flex-col gap-3 list-none p-0 m-0">
              {current.results.map((r) => (
                <ResultLine key={r.caseId} result={r} />
              ))}
            </ul>
          </div>
        )}
      </div>

      {editable && (
        <form className="flex flex-col gap-3 border-t border-[var(--sgds-border-color-muted)] pt-4" onSubmit={handleAdd} noValidate aria-label="Add a test case">
          <h3 className="text-base font-semibold">Add a test case</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1 text-sm">
              <label htmlFor={`${baseId}-id`} className="font-semibold">
                Case id
              </label>
              <input id={`${baseId}-id`} className="pt-native-date" value={caseId} placeholder="e.g. MSO-R4-TC01" onChange={(e) => setCaseId(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1 text-sm">
              <label htmlFor={`${baseId}-desc`} className="font-semibold">
                Description
              </label>
              <input
                id={`${baseId}-desc`}
                className="pt-native-date"
                value={description}
                placeholder="What this case proves"
                {...(bounds !== undefined && { maxLength: bounds.description })}
                onChange={(e) => setDescription(e.target.value)}
              />
              {bounds !== undefined && (
                <span className="text-xs text-[var(--sgds-body-color-subtle)]">{bounds.description - description.length} of {bounds.description} characters left.</span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1 text-sm">
            <label htmlFor={`${baseId}-from`} className="font-semibold">
              Start from an existing case
            </label>
            <select id={`${baseId}-from`} className="pt-native-date" defaultValue="" onChange={(e) => prefill(e.target.value)}>
              <option value="">Blank</option>
              {cases.map((c) => (
                <option key={c.caseId} value={c.caseId}>
                  {c.caseId}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1 text-sm">
            <label htmlFor={`${baseId}-input`} className="font-semibold">
              Case input (JSON, money written without a decimal point)
            </label>
            <textarea id={`${baseId}-input`} className="pt-native-date pt-mono" rows={10} value={inputText} placeholder="{ }" onChange={(e) => setInputText(e.target.value)} />
          </div>
          <fieldset className="border-0 p-0 m-0 flex flex-col gap-2">
            <legend className="font-semibold text-sm">Expected outcome</legend>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name={`${baseId}-exp`} checked={expectation === 'AMOUNT'} onChange={() => setExpectation('AMOUNT')} />
              An amount
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name={`${baseId}-exp`} checked={expectation === 'REFUSAL'} onChange={() => setExpectation('REFUSAL')} />
              A refusal
            </label>
            {expectation === 'AMOUNT' ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1 text-sm">
                  <label htmlFor={`${baseId}-eo`} className="font-semibold">
                    Expected officer or dependant (SGD)
                  </label>
                  <input id={`${baseId}-eo`} inputMode="decimal" className="pt-native-date" value={officer} placeholder="e.g. 23.28" onChange={(e) => setOfficer(e.target.value)} />
                </div>
                <div className="flex flex-col gap-1 text-sm">
                  <label htmlFor={`${baseId}-eg`} className="font-semibold">
                    Expected Government (SGD)
                  </label>
                  <input id={`${baseId}-eg`} inputMode="decimal" className="pt-native-date" value={government} placeholder="e.g. 131.92" onChange={(e) => setGovernment(e.target.value)} />
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-1 text-sm max-w-sm">
                <label htmlFor={`${baseId}-ek`} className="font-semibold">
                  Expected refusal
                </label>
                <select id={`${baseId}-ek`} className="pt-native-date" value={refusalKind} onChange={(e) => setRefusalKind(e.target.value)}>
                  {MSO_CALCULATION_REFUSALS.map((k) => (
                    <option key={k} value={k}>
                      {msoRefusalLabel(k)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </fieldset>
          <FormResult state={state} />
          <div>
            <SgdsButton type="submit" variant="outline" loading={state.status === 'submitting'}>
              Add test case
            </SgdsButton>
          </div>
        </form>
      )}
    </section>
  )
}
